import { addMethod, string, object, array, ref } from 'yup'
import { COUNTRY_CODE_VALIDATION } from '@bluheadless/ui-logic/src/constants'

const manageMessage = (values, message, defaultMessage) =>
	message ? (typeof message === 'function' ? message(values) : message) : defaultMessage

/** Add translations into {@link /packages/intl/src/yup.js} */
export const addValidators = (formatMessage) => {
	addMethod(string, 'telephone', function ({ defaultCountry = null } = {}, message) {
		return this.test({
			name: 'telephone',
			message: (values) =>
				manageMessage(
					values,
					message,
					formatMessage(
						{
							id: ['yup', 'string', 'telephone'].join('_'),
							defaultMessage: '{path} must be a valid phone number.',
						},
						values
					)
				),
			test: async (value, context) => {
				const isRequired = context?.schema?.spec?.presence === 'required'
				if (!value) {
					// if not required, empty is valid
					return !isRequired
				}
				try {
					// if countryCodes.length, only those countries can be valid
					const libPhoneNumber = await import('libphonenumber-js')
					if (value && value.startsWith('+')) {
						const parsedNumber = libPhoneNumber.parsePhoneNumber(value)
						return parsedNumber.isValid() || parsedNumber.isPossible()
					} else {
						if (!defaultCountry) return false
						// try if it's a valid number for the default country (user's country)
						const parsedNumber = libPhoneNumber.parsePhoneNumber(value, defaultCountry)
						return parsedNumber.isValid() || parsedNumber.isPossible()
					}
				} catch (e) {
					console.error(e)
					return false
				}
			},
		})
	})

	addMethod(object, 'atLeastOneTrue', function (message) {
		return this.test({
			name: 'atLeastOneTrue',
			message: (values) =>
				manageMessage(
					values,
					message,
					formatMessage(
						{
							id: ['yup', 'object', 'atleastonetrue'].join('_'),
							defaultMessage: 'At least one must be true',
						},
						values
					)
				),
			test: (value = {}) => {
				return Object.keys(value)
					.map((key) => value[key])
					.some((v) => {
						if (typeof v === 'object') {
							// nested object shape, with undefined/null values
							return Object.keys(v).some((k) => v[k])
						}
						return v
					})
			},
		})
	})

	addMethod(string, 'password', function (message) {
		return this.matches(/^(?=.*?[A-Z])(?=(.*[a-z])+)(?=(.*\d)+)(?=(.*\W)+)(?!.*\s).{8,}$/, (values) =>
			manageMessage(
				values,
				message,
				formatMessage(
					{
						id: ['yup', 'string', 'password'].join('_'),
						defaultMessage: `{path} must contain at least 8 characters, of which at least one uppercase letter, one lowercase, one digit and one special character.`,
					},
					values
				)
			)
		)
	})

	addMethod(string, 'isEqualTo', function (ref, message) {
		return this.test({
			name: 'isEqualTo',
			exclusive: false,
			message: (values) =>
				manageMessage(
					values,
					message,
					formatMessage(
						{
							id: ['yup', 'string', 'isequalto'].join('_'),
							defaultMessage: `{path} must be equal to {reference}`,
						},
						values
					)
				),
			params: {
				reference: ref.path,
			},
			test: function (value) {
				return value === this.resolve(ref)
			},
		})
	})

	addMethod(string, 'isDifferentFrom', function (ref, message) {
		return this.test({
			exclusive: false,
			name: 'isDifferentFrom',
			message: (values) =>
				manageMessage(
					values,
					message,
					formatMessage(
						{
							id: ['yup', 'string', 'isdifferentfrom'].join('_'),
							defaultMessage: `{path} must be different from {reference}`,
						},
						values
					)
				),
			params: {
				reference: ref.path,
			},
			test: function (value) {
				return value !== this.resolve(ref)
			},
		})
	})

	addMethod(string, 'isAlpha', function (message) {
		// eslint-disable-next-line regexp/no-obscure-range
		return this.matches(/^$|[A-Za-zÀ-ÖØ-öø-ÿ ']+$/, (values) =>
			manageMessage(
				values,
				message,
				formatMessage(
					{
						id: ['yup', 'string', 'isalpha'].join('_'),
						defaultMessage: `Please use only letters (a-z or A-Z) and spaces. No other characters are allowed.`,
					},
					values
				)
			)
		)
	})

	addMethod(string, 'isAlphaPunctuated', function (message) {
		// eslint-disable-next-line regexp/no-obscure-range
		return this.matches(/^$|[A-Za-zÀ-ÖØ-öø-ÿ\d.,;?¿¡'\n -"]+$/, (values) =>
			manageMessage(
				values,
				message,
				formatMessage(
					{
						id: ['yup', 'string', 'isalphapunctuated'].join('_'),
						defaultMessage: `Please use only letters (a-z or A-Z), spaces and punctuation. No other characters are allowed.`,
					},
					values
				)
			)
		)
	})

	addMethod(array, 'isAtLeastOneNotEmpty', function (message) {
		return this.test({
			name: 'isAtLeastOneNotEmpty',
			message: (values) =>
				manageMessage(
					values,
					message,
					formatMessage(
						{
							id: ['yup', 'object', 'atleastonenotempty'].join('_'),
							defaultMessage: '{path} is a required field',
						},
						values
					)
				),
			test: (items) => {
				return items?.some((item) => !!item)
			},
		})
	})

	addMethod(string, 'vatNumber', function (message) {
		return this.test({
			name: 'vatNumber',
			message: (values) =>
				manageMessage(
					values,
					message,
					formatMessage(
						{
							id: ['yup', 'string', 'vatnumber'].join('_'),
							defaultMessage: 'Invalid VAT Number',
						},
						values
					)
				),
			test: async (value, context) => {
				const { country } = context?.options?.context ?? {}

				if (!country || !value) {
					return true
				}

				const countryCodeValidation = COUNTRY_CODE_VALIDATION[country?.toLowerCase()] ?? country

				try {
					const { checkVAT, countries } = await import('jsvat')
					const { isValid, isSupportedCountry } = checkVAT(`${countryCodeValidation.toUpperCase()}${value}`, countries)

					return isValid || !isSupportedCountry
				} catch {
					return false
				}
			},
		})
	})

	addMethod(string, 'nationalIdentificationNumber', function (message) {
		return this.test({
			name: 'nationalIdentificationNumber',
			message: (values) =>
				manageMessage(
					values,
					message,
					formatMessage(
						{
							id: ['yup', 'string', 'nationalIdentificationNumber'].join('_'),
							defaultMessage: 'Invalid National Identification Number',
						},
						values
					)
				),
			test: async (value, context) => {
				const { country } = context?.options?.context ?? {}

				if (!country || !value) {
					return true
				}

				try {
					switch (country.toUpperCase()) {
						case 'IT': {
							const CodiceFiscale = (await import('codice-fiscale-js')).default
							return CodiceFiscale.check(value)
						}

						default:
							return true
					}
				} catch {
					return false
				}
			},
		})
	})

	addMethod(string, 'atLeastOneFilledBasedOnCondition', function (fields = [], message) {
		return this.test({
			name: 'atLeastOneFilledBasedOnCondition',
			message: (values) =>
				manageMessage(
					values,
					message,
					formatMessage(
						{
							id: ['yup', 'string', 'atLeastOneFilledBasedOnCondition'].join('_'),
							defaultMessage: `{label} or other fields must be filled`,
						},
						values
					)
				),
			test: function oneFilledTest(value, context) {
				const { atLeastOneFilledCondition: condition = () => false } = context?.options?.context ?? {}
				const refs = fields.map((field) => ref(field))
				const values = refs.map((_ref) => this.resolve(_ref))
				const fieldRefsAndValues = fields.reduce((acc, field, i) => {
					acc[field] = {
						ref: refs[i],
						value: values[i],
					}
					return acc
				}, {})

				if (condition(fieldRefsAndValues)) {
					return values.some((value) => !!value)
				} else {
					return true
				}
			},
		})
	})

	addMethod(array, 'fileUpload', function (message) {
		return this.test({
			name: 'fileUpload',
			message: (values) =>
				manageMessage(
					values,
					message,
					formatMessage(
						{
							id: ['yup', 'object', 'fileUpload'].join('_'),
							defaultMessage: '{path} is a required field',
						},
						values
					)
				),
			test: async (value, context) => {
				const isRequired = context?.schema?.spec?.presence === 'required'
				if (isRequired) {
					return value && value.length > 0
				} else {
					return true
				}
			},
		})
	})
}
